import React, { useEffect, useState, useCallback } from "react";
import SponsorshipTable from "../SponsorshipTable";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../../common/AuthContext";
import "./style.scss";
import Input from "../../../CoreComponent/Input";
import { Grid } from "@mui/material";

/* ---------- helpers ---------- */
const extractArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;

  const directKeys = ["boothCosts", "booth_costs", "data", "result", "results"];
  for (const k of directKeys) {
    if (Array.isArray(raw?.[k])) return raw[k];
  }

  const nestedCandidates = [
    raw?.data?.boothCosts,
    raw?.data?.booth_costs,
    raw?.data?.data,
    raw?.data?.data?.boothCosts,
    raw?.data?.data?.booth_costs,
  ];

  for (const c of nestedCandidates) {
    if (Array.isArray(c)) return c;
  }

  return [];
};

const toNumberArray = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0);

const getFloorPlanRecord = (responseData) => {
  const candidates = [
    responseData?.data?.[0],
    responseData?.data,
    responseData?.result?.[0],
    responseData?.result,
    responseData?.results?.[0],
    responseData?.results,
    Array.isArray(responseData) ? responseData?.[0] : null,
  ];

  return candidates.find((item) => item && typeof item === "object") || null;
};

const getModifiedUrl = (url) => {
  if (!url || typeof url !== "string") return null;

  const marker = "https://panel.eventscons.com/public/storage/";
  if (url.includes(marker)) {
    const afterPublic = url.split(marker)[1];
    return `https://eventscons.com/eventbackendnew/storage/app/public/${afterPublic}`;
  }

  return url;
};

/* ---------- components ---------- */
const SponsorshipOption = ({
  id,
  title,
  description,
  price,
  onSelect,
  selectedOptions,
}) => {
  const selected = selectedOptions?.includes(id);

  return (
    <div
      className={`sponsorship-option-container0 ${selected ? "selected" : ""}`}
      onClick={() => onSelect(id, !selected)}
      style={{ padding: "20px", cursor: "pointer" }}
    >
      <div className="option-container-header">
        <h3>{title}</h3>
        <p className="option-price">
          <strong>{price}</strong>
        </p>
      </div>

      <p className="option-container-description">{description}</p>

      <div className="option-container-checkbox">
        <input
          type="checkbox"
          style={{ width: "30px", height: "30px" }}
          checked={selected}
          readOnly
        />
      </div>
    </div>
  );
};

const StandardBoothPackage = ({
  conferenceId,
  exhibitNumber,
  onExhibitNumberChange,
}) => {
  const BaseUrl = process.env.REACT_APP_BASE_URL;

  const [floorPlanUrl, setFloorPlanUrl] = useState(null);
  const [agreementFile, setAgreementFile] = useState(null);
  const [loadingFloorPlan, setLoadingFloorPlan] = useState(false);
  const [floorPlanError, setFloorPlanError] = useState("");

  const fetchFloorPlan = useCallback(async () => {
    if (!conferenceId || !BaseUrl) {
      setFloorPlanUrl(null);
      setAgreementFile(null);
      setFloorPlanError("");
      return;
    }

    try {
      setLoadingFloorPlan(true);
      setFloorPlanError("");

      const response = await axios.get(`${BaseUrl}/floor/plan/${conferenceId}`);
      console.log("Floor plan API response:", response?.data);

      const record = getFloorPlanRecord(response?.data);
      console.log("Resolved floor plan record:", record);

      const floorPlan =
        record?.floor_plan ||
        record?.floorPlan ||
        record?.floor_plan_file ||
        record?.file ||
        null;

      const agreement =
        record?.agreement_page ||
        record?.agreement ||
        record?.agreement_file ||
        null;

      const finalFloorPlanUrl = getModifiedUrl(floorPlan);
      const finalAgreementUrl = getModifiedUrl(agreement);

      setFloorPlanUrl(finalFloorPlanUrl);
      setAgreementFile(finalAgreementUrl);
    } catch (error) {
      console.error("Error fetching floor plan:", error);
      setFloorPlanUrl(null);
      setAgreementFile(null);
      setFloorPlanError("Unable to load floor plan.");
    } finally {
      setLoadingFloorPlan(false);
    }
  }, [BaseUrl, conferenceId]);

  useEffect(() => {
    fetchFloorPlan();
  }, [fetchFloorPlan]);

  return (
    <div className="booth-package">
      {/* <div className="booth-package-header7">Standard Booth Package</div> */}

      <div className="booth-package-header8">
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {loadingFloorPlan ? (
            <button
              type="button"
              className="view-floor-plans-button"
              disabled
              style={{ opacity: 0.7, cursor: "not-allowed" }}
            >
              Loading Floor Plan...
            </button>
          ) : floorPlanUrl ? (
            <a
              href={floorPlanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="view-floor-plans-btn"
            >
              <button type="button" className="view-floor-plans-button">
                View Floor Plans
              </button>
            </a>
          ) : (
            <button
              type="button"
              className="view-floor-plans-button"
              disabled
              style={{ opacity: 0.7, cursor: "not-allowed" }}
            >
              Floor Plan Not Available
            </button>
          )}

          {agreementFile && (
            <a
              href={agreementFile}
              target="_blank"
              rel="noopener noreferrer"
              className="view-floor-plans-btn"
            >
              <button type="button" className="view-floor-plans-button">
                View Agreement
              </button>
            </a>
          )}
        </div>
      </div>

      {floorPlanError && (
        <p
          style={{
            color: "red",
            marginTop: "10px",
            fontSize: "14px",
          }}
        >
          {floorPlanError}
        </p>
      )}

      <div className="input-container" style={{ marginTop: "16px" }}>
        <label htmlFor="exhibitNumber" className="input-label">
          Enter Booth Number:
        </label>
        <input
          type="text"
          id="exhibitNumber"
          placeholder="Enter Booth Number"
          className="input-field"
          value={exhibitNumber || ""}
          onChange={(e) => onExhibitNumberChange(e.target.value)}
        />
      </div>
    </div>
  );
};

const BoothCostTable = ({
  conferenceId,
  selectedBoothIds,
  onSelectBooth,
  shellSchemeSelected,
  onShellSchemeChange,
  onShellSchemePriceChange,
  onSquareMetersChange,
  shellSchemePrice,
  setShellSchemePrice,
  squareMeters,
  setSquareMeters,
}) => {
  const BaseUrl = process.env.REACT_APP_BASE_URL;

  const [boothData, setBoothData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [standDepth, setStandDepth] = useState(0);
  const [standPrice, setStandPrice] = useState(0);

  const fetchFloorPlan = useCallback(async () => {
    if (!conferenceId || !BaseUrl) return;

    try {
      const response = await axios.get(`${BaseUrl}/floor/plan/${conferenceId}`);
      console.log("BoothCostTable floor response:", response?.data);

      const record = getFloorPlanRecord(response?.data);

      const shellPrice = Number(record?.shell_scheme_price_per_sqm || 0);
      const depth = record?.space_only_stand_depth || 0;
      const price = record?.space_only_stand_price_usd || 0;

      setShellSchemePrice(shellPrice);
      setStandDepth(depth);
      setStandPrice(price);
      onShellSchemePriceChange(shellPrice);
    } catch (e) {
      console.error("Error fetching floor plan:", e);
    }
  }, [BaseUrl, conferenceId, onShellSchemePriceChange, setShellSchemePrice]);

  const fetchData = useCallback(async () => {
    if (!conferenceId) {
      setBoothData([]);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = `https://eventscons.com/eventbackendnew/api/size/table/admin/get/${conferenceId}`;
      const response = await axios.get(url);
      console.log("Booth cost API response:", response?.data);

      const list = extractArray(response?.data);
      setBoothData(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching booth costs:", err);
      setError("Error fetching data. Please try again.");
      setBoothData([]);
    } finally {
      setLoading(false);
    }
  }, [conferenceId]);

  useEffect(() => {
    fetchFloorPlan();
  }, [fetchFloorPlan]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <p>Loading data...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!Array.isArray(boothData) || boothData.length === 0) {
    return <p className="error-message">No booth cost data found.</p>;
  }

  return (
    <div className="booth-cost-table" style={{ overflowX: "auto" }}>
      <div className="booth-cost-table-header3">Booth Cost Table</div>

      <h5 className="booth-cost-table-description">
        Space only stand USD {standPrice} Per Meter - Depth = {standDepth}
      </h5>

      <div className="con-booth-cost-table-table">
        <table className="booth-cost-table-table">
          <thead>
            <tr className="table-header-row">
              <th className="table-header-cell">Booth Size (LM)</th>
              <th className="table-header-cell">Cost (USD)</th>
              <th className="table-header-cell">Lunch Invitations</th>
              <th className="table-header-cell">Name Tags</th>
              <th className="table-header-cell">Selected</th>
            </tr>
          </thead>

          <tbody>
            {boothData.map((booth) => (
              <tr key={booth.id} className="table-row">
                <td className="table-cell">{booth.size}</td>
                <td className="table-cell">{booth.cost}</td>
                <td className="table-cell">{booth.lunch_invitations}</td>
                <td className="table-cell">{booth.name_tags}</td>

                <td className="table-cell">
                  <input
                    type="checkbox"
                    style={{ width: "25px", height: "25px" }}
                    checked={selectedBoothIds?.includes(booth.id)}
                    onChange={(e) => onSelectBooth(booth.id, e.target.checked)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="shell-scheme">
        <input
          type="checkbox"
          style={{ width: "20px", height: "20px" }}
          checked={shellSchemeSelected}
          onChange={onShellSchemeChange}
        />

        <span className="shell-scheme-text">
          Additional cost for Shell Scheme Booth (special build-up booth):{" "}
          <strong className="shell-scheme-cost">
            {shellSchemePrice} USD per square meter.
          </strong>
        </span>

        {shellSchemeSelected && (
          <div className="input-group">
            <Input
              label="Enter square meters:"
              placeholder="Enter square meters:"
              inputValue={squareMeters}
              setInputValue={(val) => {
                setSquareMeters(val);
                onSquareMetersChange(val);
              }}
              required={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const SponsorSection = ({
  options,
  setOptions,
  selectedOptionIds,
  setSelectedOptionIds,
  selectedSponsorshipIds,
  setSelectedSponsorshipIds,
  chosenBooths,
  setChosenBooths,
  exhibitNumber,
  setExhibitNumber,
  shellSchemeSelected,
  setShellSchemeSelected,
  invoiceData,
  setInvoiceData,
  shellSchemePrice,
  setShellSchemePrice,
  squareMeters,
  setSquareMeters,
  handlePrevious,
  handleNext,
  invoiceData3,
}) => {
  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const { myConferenceId: ctxConferenceId, userId } = useAuth();

  const [conferenceOptions, setConferenceOptions] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState(
    () => sessionStorage.getItem("selectedConferenceId") || ""
  );
  const [conferenceLoading, setConferenceLoading] = useState(false);
  const [conferenceError, setConferenceError] = useState("");

  const effectiveConferenceId =
    Number(selectedConferenceId) || Number(ctxConferenceId) || 62;

  const getAuthToken = () => localStorage.getItem("token");

  const handleConferenceChange = (value) => {
    setSelectedConferenceId(value);
    sessionStorage.setItem("selectedConferenceId", String(value));
  };

  const handleSelectBooth = (boothId, isSelected) => {
    setChosenBooths((prevIds) => {
      const validPrevIds = Array.isArray(prevIds) ? prevIds : [];
      return isSelected
        ? [...new Set([...validPrevIds, boothId])]
        : validPrevIds.filter((id) => id !== boothId);
    });
  };

  const handleSelectOption = (id, isSelected) => {
    setSelectedOptionIds((prevIds) => {
      const validPrevIds = Array.isArray(prevIds) ? prevIds : [];
      return isSelected
        ? [...new Set([...validPrevIds, id])]
        : validPrevIds.filter((x) => x !== id);
    });
  };

  const fetchConferenceList = useCallback(async () => {
    try {
      setConferenceLoading(true);
      setConferenceError("");

      const response = await axios.get(
        "https://eventscons.com/eventbackendnew/api/conferences/all"
      );

      const rawList =
        response?.data?.data ||
        response?.data?.conferences ||
        response?.data ||
        [];

      const mappedOptions = (Array.isArray(rawList) ? rawList : [])
        .map((item) => ({
          id: Number(item?.id || item?.conference_id),
          name:
            item?.conference_name ||
            item?.name ||
            item?.title ||
            `Conference ${item?.id || item?.conference_id}`,
        }))
        .filter((item) => Number.isFinite(item.id) && item.id > 0);

      const uniqueOptions = Array.from(
        new Map(mappedOptions.map((item) => [item.id, item])).values()
      );

      setConferenceOptions(uniqueOptions);

      if (uniqueOptions.length > 0) {
        const savedConferenceId = sessionStorage.getItem("selectedConferenceId");

        if (
          savedConferenceId &&
          uniqueOptions.some((item) => item.id === Number(savedConferenceId))
        ) {
          setSelectedConferenceId(String(savedConferenceId));
        } else if (
          ctxConferenceId &&
          uniqueOptions.some((item) => item.id === Number(ctxConferenceId))
        ) {
          setSelectedConferenceId(String(ctxConferenceId));
          sessionStorage.setItem(
            "selectedConferenceId",
            String(ctxConferenceId)
          );
        } else {
          setSelectedConferenceId(String(uniqueOptions[0].id));
          sessionStorage.setItem(
            "selectedConferenceId",
            String(uniqueOptions[0].id)
          );
        }
      }
    } catch (error) {
      console.error("Error fetching conference list:", error);
      setConferenceOptions([]);
      setConferenceError("Unable to load conference list.");
    } finally {
      setConferenceLoading(false);
    }
  }, [ctxConferenceId]);

  const getSponsorshipOptions = useCallback(async () => {
    if (!effectiveConferenceId || !BaseUrl) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.get(
        `${BaseUrl}/sponsorship-options/${effectiveConferenceId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setOptions(
        Array.isArray(response?.data) ? response.data : response?.data?.data || []
      );
    } catch (error) {
      console.error("Error fetching sponsorship options:", error);
      setOptions([]);
    }
  }, [BaseUrl, effectiveConferenceId, setOptions]);

  const getInvoice = useCallback(async () => {
    if (!effectiveConferenceId || !BaseUrl) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await axios.get(`${BaseUrl}/invoice/${effectiveConferenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });

      if (res.status === 404) {
        setInvoiceData(null);
        return;
      }

      setInvoiceData(res?.data?.invoices?.[0] || null);
    } catch (e) {
      console.error("Error fetching invoice data:", e);
      setInvoiceData(null);
    }
  }, [BaseUrl, effectiveConferenceId, setInvoiceData]);

  useEffect(() => {
    fetchConferenceList();
  }, [fetchConferenceList]);

  useEffect(() => {
    setExhibitNumber(invoiceData3?.exhibit_number || "");
    setSelectedOptionIds(
      invoiceData3?.sponsorship_option_details?.map((item) => item?.id) || []
    );
    setSelectedSponsorshipIds(
      invoiceData3?.conference_sponsorship_details?.map((item) => item?.id) || []
    );
    setChosenBooths(
      invoiceData3?.booth_cost_details?.map((item) => item?.id) || []
    );
    setSquareMeters(invoiceData3?.square_meters || "");
    setShellSchemeSelected(!!invoiceData3?.square_meters);

    getSponsorshipOptions();
    getInvoice();
  }, [
    effectiveConferenceId,
    invoiceData3,
    getSponsorshipOptions,
    getInvoice,
    setExhibitNumber,
    setSelectedOptionIds,
    setSelectedSponsorshipIds,
    setChosenBooths,
    setSquareMeters,
    setShellSchemeSelected,
  ]);

  const handleSubmit = async () => {
    const token = getAuthToken();
    if (!token) {
      toast.error("Token missing. Please login again.");
      return;
    }

    if (!effectiveConferenceId) {
      toast.error("Please select conference.");
      return;
    }

    const payload = {
      user_id: Number(userId),
      conference_id: Number(effectiveConferenceId),
      conference_sponsorship_option_ids: toNumberArray(selectedSponsorshipIds),
      booth_cost_ids: toNumberArray(chosenBooths),
      sponsorship_option_ids: toNumberArray(selectedOptionIds),
      additional_cost_for_shell_scheme_booth: !!shellSchemeSelected,
      exhibit_number: exhibitNumber || null,
      shell_scheme_price:
        shellSchemeSelected && shellSchemePrice != null
          ? Number(shellSchemePrice)
          : null,
      square_meters:
        shellSchemeSelected && squareMeters !== "" && squareMeters != null
          ? Number(squareMeters)
          : null,
    };

    console.log("Submitting payload =>", payload);

    try {
      await axios.post(`${BaseUrl}/invoice`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      sessionStorage.setItem(
        "selectedConferenceId",
        String(effectiveConferenceId)
      );

      toast.success("Submitted successfully.");

      handleNext();

      // Agar tum route ke through next page bhejna chahte ho to niche wali line use kar sakte ho:
      // navigate("/next-page", { replace: true });
    } catch (error) {
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        JSON.stringify(error?.response?.data || {}) ||
        "Server error";

      console.error("Submit error:", error?.response || error);
      toast.error(serverMsg);
    }
  };

  const isAnyArrayEmpty = () =>
    (selectedSponsorshipIds?.length || 0) === 0 &&
    (chosenBooths?.length || 0) === 0 &&
    (selectedOptionIds?.length || 0) === 0;

  return (
    <div className="sponsor-section">
      <div>
        <div style={{ marginBottom: "20px" }}>
          <label
            htmlFor="conferenceSelect"
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "600",
            }}
          >
            Select Conference
          </label>

          {conferenceLoading ? (
            <p>Loading conference list...</p>
          ) : (
            <select
              id="conferenceSelect"
              value={selectedConferenceId}
              onChange={(e) => handleConferenceChange(e.target.value)}
              style={{
                width: "320px",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            >
              {conferenceOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({item.id})
                </option>
              ))}
            </select>
          )}

          {conferenceError && (
            <p style={{ color: "red", marginTop: "8px" }}>{conferenceError}</p>
          )}
        </div>

        {options && options.length > 0 && (
          <div className="header-sponsorship-opportunities">
            Sponsorship Opportunities
          </div>
        )}

        <Grid
          container
          spacing={2}
          sx={{ flexWrap: "wrap", alignItems: "stretch", display: "flex" }}
        >
          {(Array.isArray(options) ? options : []).map((option) => (
            <Grid key={option.id} item xs={12} sm={6} md={4}>
              <SponsorshipOption
                selectedOptions={selectedOptionIds}
                id={option.id}
                title={option.title}
                description={option.description}
                price={option.price}
                onSelect={handleSelectOption}
              />
            </Grid>
          ))}
        </Grid>

        <SponsorshipTable
          selectedIds={selectedSponsorshipIds}
          onSelectedSponsorshipsChange={setSelectedSponsorshipIds}
          conferenceId={effectiveConferenceId}
        />

        <BoothCostTable
          conferenceId={effectiveConferenceId}
          selectedBoothIds={chosenBooths}
          onSelectBooth={handleSelectBooth}
          shellSchemeSelected={shellSchemeSelected}
          onShellSchemeChange={(e) => setShellSchemeSelected(e.target.checked)}
          onShellSchemePriceChange={setShellSchemePrice}
          onSquareMetersChange={setSquareMeters}
          shellSchemePrice={shellSchemePrice}
          setShellSchemePrice={setShellSchemePrice}
          squareMeters={squareMeters}
          setSquareMeters={setSquareMeters}
        />

        <StandardBoothPackage
          conferenceId={effectiveConferenceId}
          exhibitNumber={exhibitNumber}
          onExhibitNumberChange={setExhibitNumber}
        />

        <div className="fixed-buttons">
          <button
            style={{ backgroundColor: "#9B1321" }}
            onClick={handleSubmit}
            disabled={isAnyArrayEmpty()}
            className={`next-button ${isAnyArrayEmpty() ? "disabled" : ""}`}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponsorSection;